"use client";

import { DocumentNode, useQuery } from "@apollo/client";
import { SEARCH_OFFICES_QUERY } from "@graphql/search";
import { OfficeRecord } from "@graphql/types/graphql";
import { Filter, FiltersLayout, IOfficeLocation, SearchType } from "@types";
import { FiltersForm } from "components/organisms/Form/FiltersForm";
import { DEFAULT_SEARCH_RADIUS, SEARCH_DELAY } from "consts";
import { Locale } from "consts/locale";
import { AppContext, IAppContext, ISearchContext, SearchContext } from "context";
import { useLocale } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { getDistanceFromLatLonInKm, getCmsLocale } from "utils";
import { ArticlesResults, OfficesResults } from "./components";
import s from "./Search.module.scss";

export interface SearchProps {
  query: DocumentNode;
  type: SearchType;
  filters: Filter[];
  defaultValues?: any;
  layout?: FiltersLayout;
  preview?: boolean;
}

interface FilterState {
  term?: string;
  municipality?: {
    label: string;
    value: { coordinates: { latitude: number; longitude: number } };
  };
  tag?: string;
  date?: string;
  orderBy?: string;
  radius?: number;
}

// ============================================================================
// PURE FUNCTIONS - Deterministic, testable, no side effects
// ============================================================================

function parseFiltersFromUrl(searchParams: URLSearchParams, defaults: any): FilterState {
  const params: FilterState = { ...defaults };

  const term = searchParams.get("term");
  const tag = searchParams.get("tag");
  const date = searchParams.get("date");
  const orderBy = searchParams.get("orderBy");
  const radius = searchParams.get("radius");
  const municipalityName = searchParams.get("municipalityName");
  const municipalityCoords = searchParams.get("municipalityCoords");

  if (term) params.term = term;
  if (tag) params.tag = tag;
  if (date) params.date = date;
  if (orderBy) params.orderBy = orderBy;
  if (radius) params.radius = parseInt(radius, 10);

  if (municipalityName && municipalityCoords) {
    const [latitude, longitude] = municipalityCoords.split(",").map(parseFloat);
    params.municipality = {
      label: municipalityName,
      value: { coordinates: { latitude, longitude } },
    };
  }

  return params;
}

function buildUrlFromFilters(filters: FilterState, defaults: any): string {
  const params = new URLSearchParams();

  if (filters.term && filters.term !== defaults.term) {
    params.set("term", filters.term);
  }
  if (filters.tag && filters.tag !== defaults.tag) {
    params.set("tag", filters.tag);
  }
  if (filters.date && filters.date !== defaults.date) {
    params.set("date", filters.date);
  }
  if (filters.orderBy && filters.orderBy !== defaults.orderBy) {
    params.set("orderBy", filters.orderBy);
  }
  if (filters.radius && filters.radius !== defaults.radius) {
    params.set("radius", filters.radius.toString());
  }
  if (filters.municipality) {
    params.set("municipalityName", filters.municipality.label);
    params.set(
      "municipalityCoords",
      `${filters.municipality.value.coordinates.latitude},${filters.municipality.value.coordinates.longitude}`,
    );
  }

  return params.toString();
}

function filterOfficesByLocation(
  offices: OfficeRecord[],
  location: { latitude: number; longitude: number } | null,
  radius: number,
): OfficeRecord[] {
  if (!location) return offices;

  return offices
    .map((office) => {
      // Calculate distances for all locations
      const locationsWithDistance = office.officeLocations?.map((officeLocation) => {
        const loc = officeLocation as IOfficeLocation;
        return {
          ...officeLocation,
          distance: getDistanceFromLatLonInKm(
            loc.officeAddress?.latitude || 0,
            loc.officeAddress?.longitude || 0,
            location.latitude,
            location.longitude,
          ),
        };
      });

      return { ...office, officeLocations: locationsWithDistance };
    })
    .filter((office) => {
      // Keep only offices with at least one location within radius
      return office.officeLocations?.some((loc: any) => loc.distance < radius) ?? false;
    })
    .sort((a, b) => {
      // Sort by closest location
      const distA = (a.officeLocations?.[0] as any)?.distance ?? Infinity;
      const distB = (b.officeLocations?.[0] as any)?.distance ?? Infinity;
      return distA - distB;
    });
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const Search: FC<SearchProps> = ({
  query,
  type,
  filters,
  defaultValues = {},
  layout,
  preview = false,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale() as Locale;
  const { searchData, setSearchData } = useContext(SearchContext) as ISearchContext;
  const { setSearchedByLocation } = useContext(AppContext) as IAppContext;

  // SINGLE SOURCE OF TRUTH: URL params
  const currentFilters = useMemo(
    () => parseFiltersFromUrl(searchParams, defaultValues),
    [searchParams, defaultValues],
  );

  // Form state (controlled by currentFilters)
  const { control, setValue, reset, watch, register } = useForm({
    values: currentFilters, // Form always reflects URL
  });

  // Build GraphQL variables (server-side filtering only)
  const queryVariables = useMemo(() => {
    const baseFilter: any = {
      blockSearchIndexing: { eq: "false" },
      _locales: { allIn: [getCmsLocale(locale)] },
    };

    if (currentFilters.term) {
      baseFilter.title = { matches: { pattern: currentFilters.term } };
    }

    if (currentFilters.date) {
      const [start, end] = currentFilters.date.split(":");
      baseFilter.date = { gte: start, lte: end };
    }

    if (type === SearchType.Offices) {
      baseFilter.parent = { exists: "false" };
      baseFilter.hideOfficeFromOverview = { eq: "false" };
    }

    if (type === SearchType.Articles && currentFilters.tag) {
      baseFilter.OR = [
        { tag: { eq: currentFilters.tag } },
        { secondaryTags: { anyIn: [currentFilters.tag] } },
      ];
    }

    // For offices with location filter, fetch all for client-side filtering
    // For offices without location filter, use server-side pagination
    const isLocationFiltered = type === SearchType.Offices && currentFilters.municipality;

    return {
      filter: baseFilter,
      orderBy: currentFilters.orderBy ? [currentFilters.orderBy] : undefined,
      first: isLocationFiltered ? 100 : searchData.itemsPerPage,
      skip: isLocationFiltered ? 0 : searchData.offset,
      locale: getCmsLocale(locale),
      preview,
    };
  }, [currentFilters, type, searchData, locale, preview]);

  // Fetch data (paginated for list)
  const { data, loading, error } = useQuery(query, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  // Fetch all published offices for the map when list is paginated (no municipality filter)
  const shouldFetchAllOfficesForMap = type === SearchType.Offices && !currentFilters.municipality;
  const mapQueryVariables = useMemo(
    () => (shouldFetchAllOfficesForMap ? { ...queryVariables, first: 100, skip: 0 } : undefined),
    [shouldFetchAllOfficesForMap, queryVariables],
  );
  const { data: allOfficesForMapData } = useQuery(SEARCH_OFFICES_QUERY, {
    variables: mapQueryVariables,
    skip: !mapQueryVariables,
    fetchPolicy: "cache-and-network",
  });

  // Calculate filtered offices (before pagination)
  const filteredOffices = useMemo(() => {
    if (type !== SearchType.Offices) return [];

    const allOffices = data?.results || [];
    return filterOfficesByLocation(
      allOffices,
      currentFilters.municipality?.value?.coordinates || null,
      currentFilters.radius || DEFAULT_SEARCH_RADIUS,
    );
  }, [data?.results, type, currentFilters.municipality, currentFilters.radius]);

  // Update search context
  useEffect(() => {
    setSearchedByLocation(!!currentFilters.municipality?.value?.coordinates);
  }, [currentFilters.municipality, setSearchedByLocation]);

  // Reset pagination when filters change
  useEffect(() => {
    if (searchData.offset !== 0) {
      setSearchData({
        offset: 0,
        itemsPerPage: searchData.itemsPerPage,
        total: searchData.total,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentFilters.term,
    currentFilters.municipality,
    currentFilters.tag,
    currentFilters.date,
    currentFilters.orderBy,
    currentFilters.radius,
  ]);

  // All offices for the map: when municipality filter use filtered list; otherwise use dedicated "all offices" query
  const mapOffices = useMemo(() => {
    if (type !== SearchType.Offices) return [];
    if (currentFilters.municipality) return filteredOffices;
    return allOfficesForMapData?.results ?? [];
  }, [type, currentFilters.municipality, filteredOffices, allOfficesForMapData?.results]);

  // Apply pagination to results (uses safe offset that won't exceed bounds)
  const results = useMemo(() => {
    if (type === SearchType.Articles) {
      return data?.results || [];
    }

    if (type === SearchType.Offices) {
      // Apply client-side pagination for location-filtered results
      if (currentFilters.municipality) {
        // If offset is beyond available results, start from beginning
        const safeOffset = searchData.offset < filteredOffices.length ? searchData.offset : 0;
        const start = safeOffset;
        const end = start + searchData.itemsPerPage;
        return filteredOffices.slice(start, end);
      }

      return filteredOffices;
    }

    return [];
  }, [
    data?.results,
    type,
    currentFilters.municipality,
    filteredOffices,
    searchData.offset,
    searchData.itemsPerPage,
  ]);

  // Update total count based on whether we're using client-side filtering
  useEffect(() => {
    const isLocationFiltered = type === SearchType.Offices && currentFilters.municipality;
    const newTotal = isLocationFiltered ? results.length : data?.meta?.count || 0;

    if (newTotal !== searchData.total) {
      setSearchData({
        offset: searchData.offset,
        itemsPerPage: searchData.itemsPerPage,
        total: newTotal,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.meta?.count, results.length, type, currentFilters.municipality]);

  // Update URL when form changes (with conditional debouncing)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const subscription = watch((formData, { name }) => {
      if (timer) clearTimeout(timer);

      // Only delay for text input (term field), instant for everything else
      const isTextInput = name === "term";
      const delay = isTextInput ? SEARCH_DELAY : 0;

      timer = setTimeout(() => {
        const params = new URLSearchParams(
          buildUrlFromFilters(formData as FilterState, defaultValues),
        );

        const currentView = searchParams.get("view");
        if (currentView === "list" || currentView === "map") params.set("view", currentView);

        const finalUrl = params.toString();
        if (finalUrl !== searchParams.toString()) {
          router.replace(`${pathname}?${finalUrl}`, { scroll: false });
        }
      }, delay);
    });

    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [watch, router, pathname, searchParams, defaultValues]);

  const handleReset = useCallback(() => {
    router.replace(pathname, { scroll: false });
  }, [router, pathname]);

  return (
    <div className={s.Search}>
      <FiltersForm
        filters={filters}
        register={register}
        control={control}
        setValue={setValue}
        layout={layout}
      />

      {error && <div className={s.Error}>{error.message}</div>}

      {!error && (
        <>
          {type === SearchType.Offices && (
            <OfficesResults
              offices={results}
              mapOffices={mapOffices}
              loading={loading}
              reset={handleReset}
            />
          )}
          {type === SearchType.Articles && (
            <ArticlesResults articles={results} loading={loading} reset={handleReset} />
          )}
        </>
      )}
    </div>
  );
};
